/**
 * AuthProvider — singleton auth state for the customer web portal.
 *
 * Mirrors the React Native client's `AuthProvider` pattern:
 *   - Subscribes once to `onAuthStateChanged`
 *   - Exposes `useAuth()` hook returning `{ user, loading }`
 *   - All child screens consume the hook (no per-screen listeners)
 *
 * The signed-in `User` IS the same Firebase user the mobile app
 * uses — so role / org / subscription state propagates across both
 * surfaces via the existing Firestore listeners.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { arrayUnion, doc, serverTimestamp, setDoc } from 'firebase/firestore';

import { auth, db, onAuthStateChanged, type User } from '@/lib/firebase';
import { setSentryUser } from '@/lib/sentry';

type AuthContextValue = {
  /** Currently signed-in Firebase user, or null when signed out. */
  user: User | null;
  /** True until the first `onAuthStateChanged` callback fires. Use
   *  this to hold UI behind a splash so signed-in users never flash
   *  the sign-in page on cold start. */
  loading: boolean;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
});

/** Last time this user used the web portal — shown in the admin
 *  portal. At most one write per browser session. Best-effort. */
const recordedWebSessions = new Set<string>();
function recordWebSession(uid: string): void {
  if (recordedWebSessions.has(uid)) return;
  recordedWebSessions.add(uid);
  setDoc(
    doc(db, 'users', uid),
    { webLastSeenAt: serverTimestamp(), platforms: arrayUnion('web') },
    { merge: true },
  ).catch((err) => console.warn('[auth] recordWebSession skipped:', err));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // `onAuthStateChanged` fires once with the cached user (or null)
    // on every page load — typically within ~100ms. After that it
    // fires on sign-in / sign-out / token refresh.
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      // Tag every subsequent Sentry event with the signed-in uid.
      // Phone / email are deliberately NOT sent — we only need to
      // identify the org context, not the individual.
      setSentryUser(u?.uid ?? null);
      if (u) recordWebSession(u.uid);
    });
    return unsub;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
